// src/client/screens/auth/KYCScreen.tsx
import { useRef, useState, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Camera, Check, ChevronLeft, Shield } from 'lucide-react';
import { ROUTES } from '@/constants/routes';
import { NRC_REGEX } from '@/constants/config';
import { useAuth } from '@client/auth/useAuth';
import Button from '@client/components/Button';
import { useToast } from '@client/components/ToastProvider';
import { submitKyc } from '@client/services/kycService';

async function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(typeof r.result === 'string' ? r.result : '');
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}

const ICON_BTN: React.CSSProperties = {
  background: 'transparent',
  border: '1px solid var(--c-border)',
  width: 38,
  height: 38,
  borderRadius: 12,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--c-text)',
  cursor: 'pointer',
};

interface CaptureRow {
  id: 'front' | 'back' | 'selfie';
  label: string;
  n: string;
}

export default function KYCScreen() {
  const [front, setFront] = useState('');
  const [back, setBack] = useState('');
  const [selfie, setSelfie] = useState('');
  const [nrc, setNrc] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();
  const { refresh } = useAuth();
  const frontInputRef = useRef<HTMLInputElement>(null);
  const backInputRef = useRef<HTMLInputElement>(null);
  const selfieInputRef = useRef<HTMLInputElement>(null);

  const onFront = async (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]; if (!f) return;
    setFront(await fileToDataUrl(f));
  };
  const onBack = async (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBack(await fileToDataUrl(f));
  };
  const onSelfie = async (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]; if (!f) return;
    setSelfie(await fileToDataUrl(f));
  };

  const submit = async () => {
    if (!NRC_REGEX.test(nrc.trim())) { toast.show('NRC format: 123456/78/9', 'error'); return; }
    if (!front || !back) { toast.show('Upload both NRC photos', 'error'); return; }
    if (!selfie) { toast.show('Take a selfie to continue', 'error'); return; }
    setSubmitting(true);
    try {
      const r = await submitKyc({ frontDataUrl: front, backDataUrl: back, selfieDataUrl: selfie, nrc });
      if (!r.ok) { toast.show('KYC submission failed', 'error'); return; }
      toast.show('KYC received — we will verify shortly', 'success');
      await refresh();
      navigate(ROUTES.home, { replace: true });
    } finally {
      setSubmitting(false);
    }
  };

  const rows: CaptureRow[] = [
    { id: 'front',  label: 'NRC — Front', n: '01' },
    { id: 'back',   label: 'NRC — Back',  n: '02' },
    { id: 'selfie', label: 'Selfie',       n: '03' },
  ];

  const captured = (id: CaptureRow['id']) =>
    id === 'front' ? !!front : id === 'back' ? !!back : !!selfie;
  const triggerCapture = (id: CaptureRow['id']) => {
    if (id === 'front') frontInputRef.current?.click();
    else if (id === 'back') backInputRef.current?.click();
    else selfieInputRef.current?.click();
  };

  return (
    <div style={{
      minHeight: '100dvh',
      background: 'var(--c-bg)',
      display: 'flex',
      flexDirection: 'column',
      maxWidth: 480,
      margin: '0 auto',
    }}>
      {/* Header */}
      <div style={{ padding: '14px 20px 8px', display: 'flex', alignItems: 'center', gap: 10, minHeight: 44 }}>
        <button type="button" aria-label="Go back" style={ICON_BTN} onClick={() => navigate(-1)}>
          <ChevronLeft size={18} />
        </button>
        <span style={{ fontFamily: 'var(--ff-mono)', fontSize: 13, color: 'var(--c-text-muted)' }}>
          verify identity
        </span>
      </div>

      <div style={{ padding: '24px 28px', flex: 1 }}>
        <h1 style={{
          fontFamily: 'var(--ff-display)',
          fontSize: 44,
          lineHeight: 1,
          color: 'var(--c-text)',
          letterSpacing: -1,
          fontWeight: 400,
          margin: 0,
        }}>
          Snap your<br />NRC.
        </h1>
        <p style={{ marginTop: 14, fontSize: 14, color: 'var(--c-text-muted)' }}>
          Required by the Bank of Zambia. Encrypted on your device, encrypted on ours.
        </p>

        {/* Hidden file inputs trigger native camera */}
        <input
          ref={frontInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={onFront}
          aria-label="NRC front photo"
          style={{ display: 'none' }}
        />
        <input
          ref={backInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={onBack}
          aria-label="NRC back photo"
          style={{ display: 'none' }}
        />
        <input
          ref={selfieInputRef}
          type="file"
          accept="image/*"
          capture="user"
          onChange={onSelfie}
          aria-label="Selfie photo"
          style={{ display: 'none' }}
        />

        <div style={{ marginTop: 32, display: 'grid', gap: 12 }}>
          {rows.map((step) => {
            const isDone = captured(step.id);
            return (
              <button
                key={step.id}
                type="button"
                onClick={() => triggerCapture(step.id)}
                style={{
                  width: '100%',
                  border: `1px solid ${isDone ? 'var(--c-accent)' : 'var(--c-border)'}`,
                  background: isDone ? 'var(--c-accent-soft)' : 'var(--c-surface)',
                  borderRadius: 16,
                  padding: 18,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16,
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <span style={{
                  width: 48, height: 48, borderRadius: 12,
                  background: isDone ? 'var(--c-accent)' : 'var(--c-surface-alt)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: isDone ? 'var(--c-accent-ink)' : 'var(--c-text)',
                }}>
                  {isDone ? <Check size={22} strokeWidth={2.4} /> : <Camera size={20} />}
                </span>
                <span style={{ flex: 1 }}>
                  <span style={{
                    display: 'block',
                    fontSize: 11,
                    color: 'var(--c-text-muted)',
                    fontFamily: 'var(--ff-mono)',
                    letterSpacing: 1,
                  }}>{step.n}</span>
                  <span style={{
                    display: 'block',
                    fontSize: 16,
                    color: 'var(--c-text)',
                    fontWeight: 500,
                    marginTop: 2,
                  }}>{step.label}</span>
                </span>
                <span style={{
                  fontSize: 12,
                  color: isDone ? 'var(--c-accent)' : 'var(--c-text-muted)',
                  fontWeight: 500,
                }}>
                  {isDone ? 'Done' : 'Tap'}
                </span>
              </button>
            );
          })}

          {/* NRC number input — also card-like for consistency */}
          <div style={{
            border: `1px solid ${nrc.trim() ? 'var(--c-accent)' : 'var(--c-border)'}`,
            background: nrc.trim() ? 'var(--c-accent-soft)' : 'var(--c-surface)',
            borderRadius: 16,
            padding: 18,
          }}>
            <span style={{
              display: 'block',
              fontSize: 11,
              color: 'var(--c-text-muted)',
              fontFamily: 'var(--ff-mono)',
              letterSpacing: 1,
            }}>04</span>
            <label htmlFor="nrc-input" style={{
              display: 'block',
              fontSize: 16,
              color: 'var(--c-text)',
              fontWeight: 500,
              marginTop: 2,
              marginBottom: 8,
            }}>
              NRC number
            </label>
            <input
              id="nrc-input"
              placeholder="123456/78/9"
              value={nrc}
              onChange={(e) => setNrc(e.target.value)}
              style={{
                width: '100%',
                background: 'transparent',
                border: 'none',
                outline: 'none',
                fontSize: 18,
                fontFamily: 'var(--ff-mono)',
                color: 'var(--c-text)',
                padding: 0,
              }}
            />
          </div>
        </div>

        {/* Privacy footnote */}
        <div style={{
          marginTop: 22,
          padding: 14,
          background: 'var(--c-surface-alt)',
          border: '1px solid var(--c-border)',
          borderRadius: 12,
          display: 'flex',
          gap: 10,
          alignItems: 'flex-start',
        }}>
          <Shield size={16} color="var(--c-text-sec)" />
          <span style={{ fontSize: 12, color: 'var(--c-text-sec)', lineHeight: 1.45 }}>
            Photos never leave Thandizo. Auto-deleted 90 days after loan repaid.
          </span>
        </div>
      </div>

      <div style={{ padding: '16px 24px 28px' }}>
        <Button
          block size="lg" loading={submitting} onClick={submit}
          rightIcon={<ArrowRight size={18} />}
        >
          Continue
        </Button>
      </div>
    </div>
  );
}
